import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Hand, Scissors, Grab, Package, Gift, Egg, Rocket, CarFront } from "lucide-react";
import { Card, CardBack } from "@/components/casual/PlayingCard";
import { SlotSymbol } from "@/components/slot/SlotSymbol";
import { sfx } from "@/lib/sound";
import { Die, Pick, MultTag, Stage } from "./parts";

const FELT = "radial-gradient(ellipse at center, #166534 0%, #0b3b20 70%, #052e16 100%)";
const NIGHT = "radial-gradient(circle at 50% 110%, #1e3a8a 0%, #0b1026 50%, #050505 85%)";
const RUNNER_COLORS = ["#ef4444", "#3b82f6", "#22c55e", "#eab308", "#a855f7", "#f97316"];

const useRoll = (on, max, ms = 70) => {
  const [v, setV] = useState(1);
  useEffect(() => {
    if (!on) return;
    const iv = setInterval(() => { setV(1 + Math.floor(Math.random() * max)); sfx.reelTick(); }, ms);
    return () => clearInterval(iv);
  }, [on, max, ms]);
  return v;
};

const RedBlack = ({ res, phase }) => (
  <Stage bg={FELT} testid="stage-redblack">
    <motion.div key={phase} animate={phase === "anim" ? { rotateY: [0, 180, 360], y: [0, -30, 0] } : {}} transition={{ repeat: phase === "anim" ? Infinity : 0, duration: 0.6 }} style={{ transformPerspective: 800 }}>
      {phase === "done" && res ? <Card c={res.card} size="lg" testid="redblack-card" /> : <CardBack size="lg" />}
    </motion.div>
  </Stage>
);

const RPS_ICON = { rock: Grab, paper: Hand, scissors: Scissors };
const RPS_NAME = { rock: "Daş", paper: "Kağız", scissors: "Qayçı" };
const RpsSide = ({ k, label, shake }) => {
  const I = RPS_ICON[k];
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-xs uppercase tracking-[0.25em] text-white/60">{label}</p>
      <motion.div animate={shake ? { y: [0, -24, 0] } : { scale: [1.3, 1] }} transition={shake ? { repeat: Infinity, duration: 0.4 } : { duration: 0.3 }}
        className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#7a5c12] flex items-center justify-center shadow-[0_0_40px_rgba(212,175,55,0.35)]">
        <I size={52} className="text-black" />
      </motion.div>
      <p className="font-display font-bold">{RPS_NAME[k]}</p>
    </div>
  );
};
const Rps = ({ res, phase, choice }) => {
  const shake = phase === "anim";
  return (
    <Stage testid="stage-rps">
      <div className="flex items-center gap-6 sm:gap-16">
        <RpsSide k={choice} label="Siz" shake={shake} />
        <span className="font-display font-black text-3xl text-[#FFD700]">VS</span>
        <RpsSide k={phase === "done" && res ? res.house : "rock"} label="Kazino" shake={shake} />
      </div>
    </Stage>
  );
};

const Cups = ({ res, phase, onPick, busy }) => (
  <Stage bg="radial-gradient(ellipse at center, #7B001C 0%, #2a0a12 70%, #0a0a0a 100%)" testid="stage-cups">
    <div className="flex gap-4 sm:gap-10 items-end h-48">
      {[0, 1, 2].map((i) => {
        const up = phase === "done" && res;
        return (
          <div key={i} className="relative flex flex-col items-center">
            <Pick disabled={busy} onClick={() => onPick(i)} testid={`cup-${i}`}>
              <motion.div animate={phase === "anim" ? { x: [0, (1 - i) * 60, 0, (i - 1) * 40, 0] } : up ? { y: -70 } : { y: 0 }} transition={phase === "anim" ? { repeat: Infinity, duration: 0.8 } : { duration: 0.4 }}
                className={`w-20 h-24 sm:w-28 sm:h-32 bg-gradient-to-b from-[#FFD700] to-[#8a6a12] shadow-2xl ${res?.choice === i && up ? "ring-4 ring-white" : ""}`} style={{ clipPath: "polygon(18% 0, 82% 0, 100% 100%, 0 100%)", borderRadius: "12px" }} />
            </Pick>
            <span className={`absolute bottom-0 w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-red-700 shadow-[0_0_20px_#ef4444] transition-opacity ${up && res.ball === i ? "opacity-100" : "opacity-0"}`} />
          </div>
        );
      })}
    </div>
  </Stage>
);

const Boxes = ({ n, Icon, color, testid, cols }) => ({ res, phase, onPick, busy, choice }) => (
  <Stage testid={testid}>
    <div className={`grid gap-3 sm:gap-5 ${cols}`}>
      {[...Array(n).keys()].map((i) => {
        const open = phase === "done" && res;
        const v = open ? res.values[i] : null;
        const mine = choice === i;
        return (
          <Pick key={i} disabled={busy} onClick={() => onPick(i)} active={open && mine} testid={`${testid}-item-${i}`}
            className={`w-20 h-20 sm:w-28 sm:h-28 ${open ? (mine ? "bg-[#D4AF37]/25" : "bg-white/5 opacity-60") : "bg-white/5 hover:bg-white/10"}`}>
            <motion.div animate={phase === "anim" && mine ? { rotate: [0, -8, 8, 0] } : open && mine ? { scale: [1, 1.3, 1] } : {}} transition={{ repeat: phase === "anim" ? Infinity : 0, duration: 0.3 }}>
              <Icon size={40} style={{ color }} />
            </motion.div>
            {open && <MultTag m={v} win={v > 1} />}
          </Pick>
        );
      })}
    </div>
  </Stage>
);

const Race = ({ n, car }) => ({ res, phase }) => {
  const done = phase !== "idle" && res;
  return (
    <Stage bg={car ? "linear-gradient(180deg,#111 0%,#1f2937 100%)" : "linear-gradient(180deg,#14532d 0%,#052e16 100%)"} testid={car ? "stage-carrace" : "stage-horserace"}>
      <div className="w-full space-y-2">
        {[...Array(n).keys()].map((i) => {
          const pos = done ? res.order.indexOf(i) : -1;
          const end = done ? 88 - pos * (car ? 14 : 9) : 0;
          return (
            <div key={i} className="relative h-10 sm:h-12 rounded-lg bg-black/30 border-b border-dashed border-white/20 overflow-hidden">
              <span className="absolute right-2 top-0 bottom-0 w-1 bg-[repeating-linear-gradient(0deg,#fff_0_6px,#000_6px_12px)]" />
              <motion.div initial={{ left: "0%" }} animate={{ left: `${end}%` }} transition={{ duration: done ? 1.6 + pos * 0.1 : 0.2, ease: "easeInOut" }}
                className="absolute top-1 bottom-1 flex items-center gap-1" data-testid={`runner-${i}`}>
                {car ? <CarFront size={30} style={{ color: RUNNER_COLORS[i] }} /> : <span className="w-8 h-8 rounded-full font-display font-black text-sm flex items-center justify-center text-black" style={{ background: RUNNER_COLORS[i] }}>{i + 1}</span>}
                {phase === "done" && pos === 0 && <span className="text-[#FFD700] font-bold text-xs">1-ci</span>}
              </motion.div>
            </div>
          );
        })}
      </div>
    </Stage>
  );
};

const RINGS = ["#FFD700", "#c4112f", "#166534", "#1e3a8a", "#18181b"];
const Dart = ({ res, phase }) => {
  const r = phase === "done" && res ? res.slot : null;
  const dist = r == null ? 0 : [0, 22, 45, 68, 95][r];
  const ang = (res?.bet * 997 + (res?.slot || 0) * 61) % 360 || 30;
  return (
    <Stage bg="radial-gradient(circle,#2a1a0a 0%,#0a0a0a 75%)" testid="stage-dart">
      <div className="relative w-64 h-64 sm:w-80 sm:h-80">
        {RINGS.map((c, i) => <div key={i} className="absolute rounded-full border-2 border-white/30" style={{ inset: `${(4 - i) * 12}%`, background: c, zIndex: 5 - i }} />)}
        {r != null && (
          <motion.div initial={{ scale: 3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full bg-white shadow-[0_0_20px_#fff] z-10"
            style={{ left: `${50 + Math.cos(ang) * dist * 0.5}%`, top: `${50 + Math.sin(ang) * dist * 0.5}%` }} data-testid="dart-hit" />
        )}
        {phase === "anim" && <motion.div animate={{ scale: [1, 0.9, 1] }} transition={{ repeat: Infinity, duration: 0.4 }} className="absolute inset-0 rounded-full ring-4 ring-white/40 z-10" />}
      </div>
      <div className="flex gap-2 text-[10px] sm:text-xs">{["x10", "x3", "x1.5", "x0.5", "x0"].map((t, i) => <span key={t} className="px-2 py-0.5 rounded" style={{ background: RINGS[i], color: i === 0 ? "#000" : "#fff" }}>{t}</span>)}</div>
    </Stage>
  );
};

const Basket = ({ res, phase }) => {
  const hit = phase === "done" && res?.score;
  return (
    <Stage bg="linear-gradient(180deg,#1c1917 0%,#451a03 100%)" testid="stage-basketball">
      <div className="relative w-full max-w-md h-64">
        <div className="absolute right-6 top-4 w-24 h-16 border-4 border-white/80 rounded bg-white/5" />
        <div className="absolute right-10 top-[4.5rem] w-16 h-2 rounded-full bg-orange-500" />
        <div className="absolute right-11 top-[5rem] w-14 h-10 border-x-2 border-b-2 border-white/50 rounded-b-xl" />
        <motion.div initial={{ left: "5%", top: "75%" }}
          animate={phase === "anim" ? { left: ["5%", "40%", "68%"], top: ["75%", "0%", "12%"] } : phase === "done" ? (hit ? { left: "74%", top: "40%" } : { left: "55%", top: "70%" }) : {}}
          transition={{ duration: phase === "anim" ? 1.2 : 0.4 }} className="absolute w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-700 border-2 border-black/40" data-testid="basket-ball" />
      </div>
      {phase === "done" && <p className={`font-display font-black text-3xl ${hit ? "text-emerald-300" : "text-red-300"}`}>{hit ? "SƏBƏT!" : "Qaçırdı"}</p>}
    </Stage>
  );
};

const BigNumberStage = ({ max, field, testid, bg, res, phase }) => {
  const v = useRoll(phase === "anim", max);
  return (
    <Stage bg={bg} testid={testid}>
      <motion.p key={phase} animate={phase === "done" ? { scale: [1.5, 1] } : {}} className="font-display font-black text-8xl sm:text-9xl gold-text tabular-nums" data-testid={`${testid}-value`}>
        {phase === "done" && res ? res[field] : phase === "anim" ? v : "?"}
      </motion.p>
    </Stage>
  );
};

const BigNumber = (cfg) => (p) => <BigNumberStage {...cfg} {...p} />;

const DiceStage = ({ n, testid }) => ({ res, phase }) => (
  <Stage bg={FELT} testid={testid}>
    <div className="flex gap-4">{[...Array(n).keys()].map((i) => <Die key={i} v={phase === "done" && res ? res.dice[i] : i + 1} rolling={phase === "anim"} testid={`${testid}-die-${i}`} />)}</div>
    {phase === "done" && res && <p className="font-display font-black text-3xl text-[#FFD700]">Cəm: {res.sum}</p>}
  </Stage>
);

const RocketStage = ({ res, phase, choice }) => {
  const [m, setM] = useState(1);
  useEffect(() => {
    if (!res) { setM(1); return; }
    const end = res.won ? choice : res.crash;
    const t0 = performance.now();
    let raf;
    const tick = (t) => { const k = Math.min(1, (t - t0) / 1400); setM(1 + (end - 1) * k * k); if (k < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [res, choice]);
  const crashed = phase === "done" && res && !res.won;
  return (
    <Stage bg={NIGHT} testid="stage-rocket">
      <motion.div animate={phase === "anim" || (res && phase !== "done") ? { y: [0, -10, 0] } : crashed ? { rotate: 120, y: 40, opacity: 0.4 } : {}} transition={{ repeat: crashed ? 0 : Infinity, duration: 0.3 }}>
        <Rocket size={90} className={crashed ? "text-red-500" : "text-[#FFD700]"} />
      </motion.div>
      <p className={`font-display font-black text-6xl tabular-nums ${crashed ? "text-red-500" : "text-white"}`} data-testid="rocket-mult">{(phase === "done" && res && !res.won ? res.crash : m).toFixed(2)}x</p>
      <p className="text-white/60 text-sm">Hədəf: x{choice}</p>
    </Stage>
  );
};

const Balloon = ({ res, phase, choice }) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (!res) { setP(0); return; }
    const stop = Math.min(res.pumps, res.burst_at);
    let i = 0;
    const iv = setInterval(() => { i += 1; setP(i); sfx.click(); if (i >= stop) clearInterval(iv); }, 1300 / Math.max(stop, 1));
    return () => clearInterval(iv);
  }, [res]);
  const popped = phase === "done" && res && !res.won;
  return (
    <Stage bg="radial-gradient(circle at 50% 30%, #3b0764 0%, #0a0a0a 75%)" testid="stage-balloon">
      <div className="h-64 flex items-end justify-center">
        {popped ? <motion.p initial={{ scale: 0.5 }} animate={{ scale: 1.4 }} className="font-display font-black text-5xl text-red-500">PAT!</motion.p> : (
          <motion.div animate={{ scale: 0.5 + p * 0.09 }} className="w-32 h-40 rounded-[50%] bg-gradient-to-br from-red-400 to-red-700 shadow-[inset_-12px_-12px_30px_rgba(0,0,0,0.4)] origin-bottom" data-testid="balloon" />
        )}
      </div>
      <p className="text-white/70 text-sm">Nasos: {phase === "idle" ? choice : p} / {choice}</p>
    </Stage>
  );
};

const CW = ["black", "red", "black", "blue", "black", "red", "black", "red", "black", "blue", "black", "red", "black", "red", "black", "blue", "black", "red", "black", "red", "black", "blue", "black", "red", "gold"];
const CW_HEX = { black: "#18181b", red: "#c4112f", blue: "#2563eb", gold: "#FFD700" };
const ColorWheel = ({ res, phase }) => {
  const seg = 360 / 25;
  const spots = res ? CW.map((c, i) => (c === res.color ? i : -1)).filter((i) => i >= 0) : [0];
  const target = res ? spots[res.index % spots.length] : 0;
  const rot = res ? 360 * 6 - target * seg - seg / 2 : 0;
  const grad = CW.map((c, i) => `${CW_HEX[c]} ${i * seg}deg ${(i + 1) * seg}deg`).join(",");
  return (
    <Stage testid="stage-colorwheel">
      <div className="relative w-64 h-64 sm:w-80 sm:h-80">
        <div className="absolute left-1/2 -top-2 -translate-x-1/2 w-0 h-0 border-x-[12px] border-x-transparent border-t-[22px] border-t-[#FFD700] z-10" />
        <motion.div animate={{ rotate: rot }} transition={{ duration: res ? 1.8 : 0, ease: [0.2, 0.8, 0.3, 1] }} className="w-full h-full rounded-full border-8 border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.3)]" style={{ background: `conic-gradient(${grad})` }} data-testid="colorwheel-disc" />
        <div className="absolute inset-[40%] rounded-full bg-[#111] border-4 border-[#D4AF37]" />
      </div>
    </Stage>
  );
};

const COIN_SLOTS = ["x20", "x7", "x3", "x1.5", "x0.5", "x0"];
const COIN_ORDER = [5, 3, 1, 0, 2, 4];
const CoinDrop = ({ res, phase }) => {
  const col = res ? COIN_ORDER.indexOf(res.slot) : 2.5;
  const mid = useMemo(() => [30 + Math.random() * 30, 20 + Math.random() * 50], [res]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Stage bg="radial-gradient(circle at 50% 0%, #422006 0%, #0a0a0a 75%)" testid="stage-coindrop">
      <div className="relative w-full max-w-md h-72">
        {[...Array(5).keys()].map((r) => [...Array(6).keys()].map((c) => <span key={`${r}${c}`} className="absolute w-2 h-2 rounded-full bg-white/40" style={{ left: `${8 + c * 16 + (r % 2) * 8}%`, top: `${10 + r * 14}%` }} />))}
        <motion.div key={res ? `${res.balance}-${res.demo_balance}-${res.slot}` : "idle"} initial={{ top: "0%", left: "46%" }}
          animate={res ? { top: ["0%", "30%", "55%", "82%"], left: ["46%", `${mid[0]}%`, `${mid[1]}%`, `${col * 16.6 + 4}%`] } : {}}
          transition={{ duration: 1.4 }} className="absolute w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#8a6a12] border-2 border-[#fff8] shadow-[0_0_20px_#FFD700]" data-testid="coin" />
        <div className="absolute bottom-0 left-0 right-0 grid grid-cols-6 gap-1">
          {COIN_ORDER.map((s, i) => <div key={i} className={`h-10 rounded-t-lg flex items-center justify-center text-xs font-bold ${phase === "done" && res?.slot === s ? "bg-[#FFD700] text-black" : "bg-white/10 text-white/80"}`}>{COIN_SLOTS[s]}</div>)}
        </div>
      </div>
    </Stage>
  );
};

const FRUITS = ["cherry", "lemon", "grape", "bell", "star", "seven"];
const Fruit3 = ({ res, phase }) => {
  const roll = useRoll(phase === "anim", 6, 90);
  const win = new Set((res?.lines || []).flatMap((l) => [[[0, 0], [1, 0], [2, 0]], [[0, 1], [1, 1], [2, 1]], [[0, 2], [1, 2], [2, 2]], [[0, 0], [1, 1], [2, 2]], [[0, 2], [1, 1], [2, 0]]][l.line].map(([r, k]) => `${r}${k}`)));
  return (
    <Stage bg="linear-gradient(180deg,#3b0764,#0a0a0a)" testid="stage-fruit3">
      <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 rounded-2xl bg-black/60 border-4 border-[#D4AF37]">
        {[0, 1, 2].map((r) => [0, 1, 2].map((k) => (
          <div key={`${r}${k}`} className={`w-20 h-20 sm:w-28 sm:h-28 rounded-xl flex items-center justify-center bg-gradient-to-b from-white/10 to-white/0 ${phase === "done" && win.has(`${k}${r}`) ? "ring-4 ring-[#FFD700] bg-[#FFD700]/20" : ""}`}>
            <SlotSymbol name={phase === "done" && res ? res.grid[k][r] : FRUITS[(roll + r * 2 + k) % 6]} size={48} />
          </div>
        )))}
      </div>
    </Stage>
  );
};

const Football = ({ res, phase }) => (
  <Stage bg="linear-gradient(180deg,#14532d 0%,#052e16 100%)" testid="stage-football">
    <div className="rounded-2xl bg-black/70 border border-white/10 px-6 sm:px-10 py-6 grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8 w-full max-w-lg">
      <p className="font-display font-bold text-right text-lg sm:text-2xl" data-testid="fb-home">{res?.home || "Ev sahibi"}</p>
      <motion.p key={phase} animate={phase === "anim" ? { opacity: [1, 0.3, 1] } : { scale: [1.4, 1] }} transition={{ repeat: phase === "anim" ? Infinity : 0, duration: 0.6 }}
        className="font-display font-black text-4xl sm:text-6xl text-[#FFD700] tabular-nums" data-testid="fb-score">{phase === "done" && res ? `${res.score[0]}:${res.score[1]}` : "0:0"}</motion.p>
      <p className="font-display font-bold text-lg sm:text-2xl" data-testid="fb-away">{res?.away || "Qonaq"}</p>
    </div>
    <p className="text-white/60 text-sm">{phase === "anim" ? "Matç gedir..." : phase === "done" ? "Final fiti" : "Nəticəni təxmin edin"}</p>
  </Stage>
);

const n = (k) => [...Array(k).keys()];
export const QUICK = {
  redblack: { Stage: RedBlack, choices: [{ v: "red", l: "Qırmızı", m: "x1.94", bg: "bg-red-700" }, { v: "black", l: "Qara", m: "x1.94", bg: "bg-zinc-800 text-white" }], start: sfx.flip, ms: 1200, rules: "Kartın rəngini təxmin et. ♥♦ qırmızı, ♠♣ qara.", text: (r) => (r.color === "red" ? "Qırmızı" : "Qara") },
  rps: { Stage: Rps, choices: [{ v: "rock", l: "Daş" }, { v: "paper", l: "Kağız" }, { v: "scissors", l: "Qayçı" }], ms: 1300, rules: "Qələbə x1.91, heç-heçədə mərc qaytarılır.", text: (r) => ({ win: "Qazandınız", draw: "Heç-heçə", lose: "Kazino qazandı" })[r.result] },
  cups: { Stage: Cups, choices: n(3).map((i) => ({ v: i })), pickInStage: true, ms: 1500, start: sfx.flip, rules: "Top hansı stəkanın altındadır? Tapsan x2.91.", hint: "Stəkan seçin" },
  chest: { Stage: Boxes({ n: 5, Icon: Package, color: "#D4AF37", testid: "stage-chest", cols: "grid-cols-3 sm:grid-cols-5" }), choices: n(5).map((i) => ({ v: i })), pickInStage: true, ms: 1100, rules: "5 sandıqda: x0, x0.5, x1, x1.5, x1.85. Birini seç.", hint: "Sandıq seçin" },
  horserace: { Stage: Race({ n: 6 }), choices: n(6).map((i) => ({ v: i, l: `#${i + 1}`, m: "x5.82" })), choiceLabel: "At", ms: 2200, start: sfx.whistle, rules: "6 at yarışır. Qalibi tap, x5.82 qazan.", action: "Start", text: (r) => `Qalib: #${r.winner + 1}` },
  carrace: { Stage: Race({ n: 4, car: true }), choices: n(4).map((i) => ({ v: i, l: ["Qırmızı", "Mavi", "Yaşıl", "Sarı"][i], m: "x3.88" })), choiceLabel: "Maşın", ms: 2100, rules: "4 maşın yarışır. Qalibi tap, x3.88 qazan.", action: "Start", text: (r) => `Qalib: ${["Qırmızı", "Mavi", "Yaşıl", "Sarı"][r.winner]}` },
  dart: { Stage: Dart, ms: 1000, action: "At", rules: "Mərkəz x10, daxili x3, orta x1.5, xarici x0.5.", hint: "Oxu atın" },
  basketball: { Stage: Basket, choices: [{ v: "easy", l: "Yaxın", m: "x1.29" }, { v: "medium", l: "Orta", m: "x1.94" }, { v: "hard", l: "Uzaq", m: "x3.88" }], def: 1, ms: 1600, action: "At", rules: "Məsafə seç: yaxın 75%, orta 50%, uzaq 25% şans." },
  luckynumber: { Stage: BigNumber({ max: 10, field: "number", testid: "stage-luckynumber", bg: "radial-gradient(circle,#14532d,#0a0a0a 75%)" }), choices: n(10).map((i) => ({ v: i + 1, l: `${i + 1}` })), choiceLabel: "Rəqəm", ms: 1300, rules: "1-10 arası rəqəm seç. Düşsə x9.7." },
  lucky7: { Stage: DiceStage({ n: 2, testid: "stage-lucky7" }), choices: [{ v: "under", l: "7-dən az", m: "x2.32" }, { v: "seven", l: "7", m: "x5.82" }, { v: "over", l: "7-dən çox", m: "x2.32" }], ms: 1300, action: "Zər at", rules: "İki zərin cəmini təxmin et." },
  sicbo: { Stage: DiceStage({ n: 3, testid: "stage-sicbo" }), choices: [{ v: "small", l: "Kiçik 4-10", m: "x2" }, { v: "big", l: "Böyük 11-17", m: "x2" }], ms: 1400, action: "Zər at", rules: "3 zər. Üçlük (eyni 3 zər) olarsa kazino qazanır." },
  giftbox: { Stage: Boxes({ n: 9, Icon: Gift, color: "#ef4444", testid: "stage-giftbox", cols: "grid-cols-3" }), choices: n(9).map((i) => ({ v: i })), pickInStage: true, ms: 1100, rules: "9 qutuda ən böyük uduş x3.73. Birini seç.", hint: "Qutu seçin" },
  goldenegg: { Stage: Boxes({ n: 3, Icon: Egg, color: "#FFD700", testid: "stage-goldenegg", cols: "grid-cols-3" }), choices: n(3).map((i) => ({ v: i })), pickInStage: true, ms: 1100, rules: "3 yumurta: x0, x0.91, x2. Qızıl yumurtanı tap!", hint: "Yumurta seçin" },
  rocket: { Stage: RocketStage, choices: [1.5, 2, 3, 5, 10, 25, 50, 100].map((v) => ({ v, l: `x${v}` })), def: 1, choiceLabel: "Hədəf", ms: 1500, action: "Uç", rules: "Raket hədəfə çatsa, hədəf əmsalı qədər qazanırsan.", text: (r) => `Partladı: x${r.crash}` },
  balloon: { Stage: Balloon, choices: n(10).map((i) => ({ v: i + 1, l: `${i + 1}` })), def: 2, choiceLabel: "Nasos sayı", ms: 1500, action: "Şişir", rules: "Hər nasosda 15% partlama riski. 1 nasos x1.14, 5 nasos x2.19, 10 nasos x4.92." },
  updown: { Stage: BigNumber({ max: 100, field: "number", testid: "stage-updown", bg: NIGHT }), choices: [{ v: "under", l: "Aşağı 1-49", m: "x1.98", bg: "bg-red-700" }, { v: "over", l: "Yuxarı 52-100", m: "x1.98", bg: "bg-emerald-700" }], ms: 1300, rules: "1-100 arası rəqəm düşür. 50 və 51 kazinonundur." },
  colorwheel: { Stage: ColorWheel, choices: [{ v: "black", l: "Qara", m: "x2", bg: "bg-zinc-800 text-white" }, { v: "red", l: "Qırmızı", m: "x3", bg: "bg-red-700" }, { v: "blue", l: "Mavi", m: "x6", bg: "bg-blue-700" }, { v: "gold", l: "Qızıl", m: "x24" }], ms: 1900, action: "Fırlat", rules: "25 sektor: 12 qara, 8 qırmızı, 4 mavi, 1 qızıl." },
  coindrop: { Stage: CoinDrop, ms: 1500, action: "Qəpik at", rules: "Qəpik düşdüyü xananın əmsalını qazandırır: x20-yə qədər.", hint: "Qəpiyi atın" },
  fruit3: { Stage: Fruit3, ms: 1300, action: "Fırlat", rules: "5 xətt (3 sətir + 2 diaqonal). Eyni 3 meyvə = uduş. 777 ən böyük." },
  football: { Stage: Football, choices: [{ v: "home", l: "1 · Ev", m: "x2.16" }, { v: "draw", l: "X · Heç-heçə", m: "x3.59" }, { v: "away", l: "2 · Qonaq", m: "x3.46" }], ms: 1800, start: sfx.whistle, action: "Matçı başlat", rules: "Matçın nəticəsini təxmin et (1X2)." },
};
